/**
 * NotificationService - Handles browser/push notifications
 */

class NotificationService {
  constructor() {
    this.permission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
    this.notificationPrefs = null;
  }

  /**
   * Check if browser notifications are supported
   */
  isSupported() {
    return typeof Notification !== 'undefined';
  }

  /**
   * Get current permission status
   */
  getPermission() {
    if (!this.isSupported()) return 'denied';
    return Notification.permission;
  }

  /**
   * Request permission for browser notifications
   */
  async requestPermission() {
    if (!this.isSupported()) {
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  /**
   * Update notification preferences from user settings
   */
  setPreferences(prefs) {
    this.notificationPrefs = prefs;
  }

  /**
   * Check if a notification type should trigger a push notification
   */
  shouldNotify(type) {
    if (this.getPermission() !== 'granted') return false;
    if (!this.notificationPrefs) return true; // Default to true if no prefs loaded
    
    const pref = this.notificationPrefs[type];
    return pref?.push === true;
  }

  /**
   * Show a browser notification
   */
  show(title, options = {}) {
    if (this.getPermission() !== 'granted') {
      console.log('[NotificationService] Permission not granted, skipping notification');
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: '/images/icon-192.png',
        badge: '/images/badge-96.png',
        ...options
      });

      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);

      // Handle click
      notification.onclick = () => {
        window.focus();
        notification.close();
        if (options.onClick) {
          options.onClick();
        }
      };

      return notification;
    } catch (error) {
      console.error('[NotificationService] Error showing notification:', error);
      return null;
    }
  }

  /**
   * Show a notification for a new message
   */
  showMessageNotification(senderName, messagePreview, onClick) {
    if (!this.shouldNotify('messages')) return null;
    
    return this.show(`New message from ${senderName}`, {
      body: messagePreview,
      tag: 'message', // Prevents duplicate notifications
      onClick
    });
  }

  /**
   * Show a notification for a new dojo message
   */
  showDojoMessageNotification(dojoName, senderName, messagePreview, onClick) {
    if (!this.shouldNotify('dojo_messages')) return null;
    
    return this.show(`${dojoName}`, {
      body: `${senderName}: ${messagePreview}`,
      tag: 'dojo-message',
      onClick
    });
  }

  /**
   * Show a notification for a friend request
   */
  showFriendRequestNotification(senderName, onClick) {
    if (!this.shouldNotify('friend_requests')) return null;
    
    return this.show('New Friend Request', {
      body: `${senderName} wants to be your friend`,
      tag: 'friend-request',
      onClick
    });
  }

  /**
   * Show a notification for a dojo invite
   */
  showDojoInviteNotification(dojoName, onClick) {
    if (!this.shouldNotify('dojo_invites')) return null;
    
    return this.show('Dojo Invitation', {
      body: `You've been invited to join ${dojoName}`,
      tag: 'dojo-invite',
      onClick
    });
  }

  /**
   * Show a notification for a Coach reaction to an activity. Deliberately
   * mirrors showMessageNotification's shape — the Coach's reaction should
   * feel like receiving a direct message from the Coach, not a separate
   * notification family. Uses a fixed tag (rather than gating on a
   * per-type preference, unlike the friend/dojo notifications above) so a
   * fast run of reactions collapses to one, not a preference the user has
   * no UI to configure yet.
   */
  showCoachReactionNotification(coachName, reaction, activityName, onClick) {
    const name = coachName || 'Your Coach';
    const emoji = reaction?.emoji ? `${reaction.emoji} ` : '';
    const label = reaction?.label || 'reacted';
    const target = activityName || 'your activity';

    return this.show(`${name} reacted`, {
      body: `${emoji}${label} — ${target}`,
      tag: 'coach-reaction', // Prevents duplicate notifications from stacking
      onClick
    });
  }

  showCoachTypingNotification(coachName = 'Your Coach') {
    return this.show(`${coachName} is typing`, {
      body: 'Your Coach is preparing feedback…',
      tag: 'coach-typing',
      silent: true,
    });
  }

  /**
   * Show a notification for a scheduled activity
   */
  showScheduledActivityNotification(activityName, onClick) {
    if (!this.shouldNotify('scheduled_activities')) return null;
    
    return this.show('Scheduled Activity', {
      body: `It's time for: ${activityName}`,
      tag: 'scheduled-activity',
      onClick
    });
  }
}

// Singleton instance
const notificationService = new NotificationService();

export default notificationService;
